import { Close as CloseIcon } from '@mui/icons-material';
import { Button, Dialog, Divider } from '@mui/material';
import { ReactComponent as BedOutlined } from 'assets/svg/feature-bedroom.svg';
import { ReactComponent as BathroomsOutlined } from 'assets/svg/feature-bathroom.svg';
import { ReactComponent as Budget } from 'assets/svg/budget.svg';
import { ReactComponent as ConstructedArea } from 'assets/svg/feature-buildarea.svg';
import { ReactComponent as LocationOnOutlined } from 'assets/svg/LocationOn.svg';
import { ReactComponent as HomeIcon } from 'assets/svg/Default-Property-Image.svg';
import { ReactComponent as AISearchSvg } from 'assets/svg/AI-Search-Icon.svg';
import { ReactComponent as LogoSearchSvg } from 'assets/svg/LogoSearch.svg';
import { ReactComponent as SaveFilterPreferenceSvg } from 'assets/svg/SaveFilterPreference.svg';

import {
    MobileFilterSection,
    MobileSimpleFilter,
    MobileRangeFilter,
    MobileBedBathFilter,
    MobileLocationFilter,
    MobilePropertyTypeFilter,
} from './Filter/MobileFilterComponents';
import { FilterType } from 'util/Types';
import { DEFAULT_FILTER, AVAILABILITY_TYPES } from 'util/Constants';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { aiSearchSchema } from 'schemas/aiSearchValidationSchema';
import usePropertiesStore from 'store/usePropertiesStore';
import { SaveFilterModal } from './Modals';
import { GetUniqueString } from 'util/Funcs';
import { LocalSavedFilter } from 'util/Types';
import { useFiltersStore } from 'store/useFiltersStore';

interface FormData {
    searchTerm: string;
}

interface MobileFilterProps {
    prevFilter: FilterType;
    onChange: (filter: FilterType) => void;
    resultCount: number;
    visible: boolean;
    onClose: () => void;
    showAvailabilityFilter?: boolean;
    isLoggedIn?: boolean;
}

const MobileFilter = ({ prevFilter, onChange, resultCount, visible, onClose, showAvailabilityFilter = false, isLoggedIn = true }: MobileFilterProps) => {
    // Local state to store working filter changes - prevents API calls on every interaction
    // Only calls parent onChange when "Apply Filters" is clicked
    const [workingFilter, setWorkingFilter] = useState<FilterType>(prevFilter);
    const [saveModalVisible, setSaveModalVisible] = useState(false);
    const { currentPage, fetchCompetitorAllPropertiesSort, loadingProperties } = usePropertiesStore();
    const { addFilter } = useFiltersStore();

    // AI Search form
    const { control, handleSubmit, getValues, setValue, clearErrors } = useForm<FormData>({
        resolver: yupResolver(aiSearchSchema),
        defaultValues: { searchTerm: workingFilter.query || '' },
        mode: 'onSubmit',
    });

    // Reset working filter when modal opens with new prevFilter
    useEffect(() => {
        if (visible) {
            setWorkingFilter({ ...prevFilter });
        }
    }, [visible, prevFilter]);

    // Cancel changes and close modal - discards all changes made in the session
    const handleClose = () => {
        setWorkingFilter({ ...prevFilter }); // Reset to original
        onClose();
    };

    // Apply changes and close modal - this is the only place that triggers API calls
    const handleApplyFilters = () => {
        const searchTerm = getValues('searchTerm');
        const finalFilter = {
            ...workingFilter,
            query: searchTerm || undefined,
        };
        onChange(finalFilter);
        onClose();
    };

    const handleFilterChange = (key: keyof FilterType, value: any) => {
        setWorkingFilter((prev) => ({ ...prev, [key]: value }));
    };

    const handleBedBathChange = (beds: number[], baths: number[]) => {
        setWorkingFilter((prev) => ({
            ...prev,
            bedbath: { bedIdx: beds, bathIdx: baths },
        }));
    };

    const handleLocationChange = (location: {
        region?: string;
        locations?: Array<{
            city: string;
            neighborhoods: string[];
        }>;
    }) => {
        setWorkingFilter((prev) => {
            const newFilter = { ...prev };

            if (location.locations && location.locations.length > 0) {
                newFilter.locations = location.locations;
                newFilter.city = location.locations.map((loc) => loc.city);
                newFilter.neighborhood = location.locations.flatMap((loc) => loc.neighborhoods);
            } else {
                newFilter.locations = [];
                newFilter.city = [];
                newFilter.neighborhood = [];
            }

            return newFilter;
        });
    };

    // AI Search handlers
    const handleInputChange = useCallback(
        (value: string, onChange: (value: string) => void) => {
            onChange(value);
            clearErrors('searchTerm');
        },
        [clearErrors],
    );

    const onClickClear = () => {
        setValue('searchTerm', '');
    };

    const handleSaveFilter = (filter: LocalSavedFilter) => {
        addFilter(filter);
        setSaveModalVisible(false);
    };

    return (
        <>
            <Dialog
                fullScreen
                open={visible}
                onClose={handleClose}
                className="!z-50"
                PaperProps={{
                    className: 'bg-gray-50',
                }}
            >
                <div className="flex flex-col h-full">
                    {/* Header */}
                    <div className="sticky top-0 z-10 bg-white border-b border-gray-200">
                        <div className="flex items-center justify-between px-4 py-3">
                            <h2 className="text-lg font-semibold text-gray-900">Filters</h2>
                            <button onClick={handleClose} className="p-2 text-gray-500 hover:text-gray-700 rounded-full hover:bg-gray-100">
                                <CloseIcon />
                            </button>
                        </div>
                        <div className="px-4 py-2 bg-gray-50 border-t border-gray-200">
                            <p className="text-sm text-gray-600">{resultCount} properties found</p>
                        </div>
                    </div>

                    {/* Content */}
                    <div className="flex-1 overflow-y-auto p-4 space-y-4">
                        {/* AI Search Section */}
                        {isLoggedIn && (
                            <MobileFilterSection title="AI Search" icon={<AISearchSvg className="w-5 h-5" />}>
                                <div className="h-[48px] w-full rounded-[8px] border border-gray-200 bg-white">
                                    <div className="flex justify-between items-center gap-[5px] h-full px-[10px]">
                                        <LogoSearchSvg />
                                        <Controller
                                            name="searchTerm"
                                            control={control}
                                            render={({ field: { value, onChange } }) => (
                                                <input
                                                    type="text"
                                                    value={value}
                                                    onChange={(e) => handleInputChange(e.target.value, onChange)}
                                                    placeholder="Seaview, Terrace, Pool, Parking, etc."
                                                    className="flex-grow bg-transparent"
                                                    disabled={loadingProperties}
                                                />
                                            )}
                                        />
                                        {getValues('searchTerm')?.length > 0 && (
                                            <div className="text-my-blue cursor-pointer text-sm" onClick={onClickClear}>
                                                Clear
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </MobileFilterSection>
                        )}

                        <MobileFilterSection title="Location" icon={<LocationOnOutlined className="w-5 h-5" />}>
                            <MobileLocationFilter
                                value={
                                    workingFilter.locations && workingFilter.locations.length > 0
                                        ? {
                                              region: workingFilter.locations[0].city,
                                              locations: workingFilter.locations,
                                          }
                                        : {}
                                }
                                onChanged={handleLocationChange}
                                placeholder="Select location"
                            />
                        </MobileFilterSection>

                        <MobileFilterSection title="Property Type" icon={<HomeIcon className="w-5 h-5" />}>
                            <MobilePropertyTypeFilter
                                value={workingFilter.type}
                                onChanged={(value: string[]) => handleFilterChange('type', value)}
                                placeholder="Select property type"
                            />
                        </MobileFilterSection>

                        <MobileFilterSection title="Bedrooms & Bathrooms">
                            <MobileBedBathFilter
                                bedIndices={workingFilter.bedbath.bedIdx}
                                bathIndices={workingFilter.bedbath.bathIdx}
                                onCallback={handleBedBathChange}
                            />
                        </MobileFilterSection>

                        <MobileFilterSection title="Budget" icon={<Budget className="w-5 h-5" />}>
                            <MobileRangeFilter
                                title="Price Range"
                                value={[workingFilter.price.from, workingFilter.price.to]}
                                onChange={(value: [number, number]) => {
                                    handleFilterChange('price', { from: value[0], to: value[1] });
                                }}
                                min={0}
                                max={50000000}
                                steps={[
                                    { max: 500000, step: 25000 },
                                    { max: 1000000, step: 50000 },
                                    { max: 5000000, step: 100000 },
                                    { max: 10000000, step: 500000 },
                                    { max: 50000000, step: 1000000 },
                                ]}
                                unit="€"
                            />
                        </MobileFilterSection>

                        <MobileFilterSection title="Area" icon={<ConstructedArea className="w-5 h-5" />}>
                            <MobileRangeFilter
                                title="Living Size"
                                value={[workingFilter.constructionArea.from, workingFilter.constructionArea.to]}
                                onChange={(value: [number, number]) => {
                                    handleFilterChange('constructionArea', { from: value[0], to: value[1] });
                                }}
                                min={0}
                                max={2000}
                                steps={[
                                    { max: 400, step: 25 },
                                    { max: 2000, step: 100 },
                                ]}
                                unit="m²"
                            />
                            <div className="mt-4">
                                <MobileRangeFilter
                                    title="Plot Area"
                                    value={[workingFilter.plotArea.from, workingFilter.plotArea.to]}
                                    onChange={(value: [number, number]) => {
                                        handleFilterChange('plotArea', { from: value[0], to: value[1] });
                                    }}
                                    min={0}
                                    max={5000}
                                    steps={[
                                        { max: 5000, step: 100 },
                                        { max: 15000, step: 1000 },
                                        { max: 100000, step: 5000 },
                                    ]}
                                    unit="m²"
                                />
                            </div>
                        </MobileFilterSection>

                        {showAvailabilityFilter && (
                            <MobileFilterSection title="Availability">
                                <div className="space-y-2">
                                    {AVAILABILITY_TYPES.map((type) => {
                                        const availability = workingFilter.availability || [];
                                        const lowercaseType = type.toLowerCase();
                                        const isSelected = availability.includes(lowercaseType);

                                        return (
                                            <div
                                                key={type}
                                                className={`flex items-center justify-between p-3 rounded-lg border ${
                                                    isSelected ? 'border-my-blue bg-blue-50' : 'border-gray-200 bg-white'
                                                }`}
                                                onClick={() => {
                                                    const currentSelection = availability;
                                                    const newSelection = currentSelection.includes(lowercaseType)
                                                        ? currentSelection.filter((item) => item !== lowercaseType)
                                                        : [...currentSelection, lowercaseType];
                                                    handleFilterChange('availability', newSelection);
                                                }}
                                            >
                                                <span className="text-sm font-medium">{type}</span>
                                                <div
                                                    className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                                                        isSelected ? 'border-my-blue bg-my-blue' : 'border-gray-300'
                                                    }`}
                                                >
                                                    {isSelected && (
                                                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                            <path
                                                                fillRule="evenodd"
                                                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                                                clipRule="evenodd"
                                                            />
                                                        </svg>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </MobileFilterSection>
                        )}

                        {/* Save Filter Button - Only show for logged in users */}
                        {isLoggedIn && (
                            <div
                                className="flex items-center gap-[5px] cursor-pointer text-my-brown p-3 rounded-lg border border-gray-200 bg-white"
                                onClick={() => setSaveModalVisible(true)}
                            >
                                <SaveFilterPreferenceSvg />
                                <span className="text-sm font-medium">Save Filter for next time</span>
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4">
                        <Button fullWidth variant="contained" onClick={handleApplyFilters} className="!bg-my-blue !text-white !py-3 !text-base !font-medium">
                            Apply Filters
                        </Button>
                    </div>
                </div>
            </Dialog>

            <SaveFilterModal
                visible={saveModalVisible}
                onClose={() => setSaveModalVisible(false)}
                onSave={handleSaveFilter}
                filter={{
                    id: GetUniqueString(),
                    title: '',
                    filter: workingFilter,
                }}
            />
        </>
    );
};

export default MobileFilter;
